<template>
  <div
    ref="segmentContainerRef"
    :key="items.length"
    class="wl-segment bg-muted relative flex items-center gap-0.5 overflow-hidden rounded-2xl p-0.5 leading-normal"
    :class="[
      `variant-${variant}`,
      { 'w-full': isFullWidth },
      { 'cursor-not-allowed opacity-50': isDisabled },
    ]"
  >
    <div
      ref="backgroundRef"
      class="active-item ease absolute inset-y-0.5 translate-x-1/2 rounded-xl duration-200"
    />
    <WLSegmentBlock
      v-for="(
        { value, icon, isDisabled: itemIsDisabled, label, to }, index
      ) in items"
      :key="index"
      :ref="setItemRef"
      :tabindex="0"
      :label="label"
      :to="to"
      :icon="icon"
      :value
      :is-active="value === modelValue"
      :is-disabled="itemIsDisabled || isDisabled"
      class="relative w-full justify-center bg-transparent"
      :class="{
        'is-active': value === modelValue,
        'min-w-max': !isFullWidth,
      }"
      @click="onChange(value)"
      @keyup.enter="onChange(value)"
      @keyup.space="onChange(value)"
    />
  </div>
</template>

<script lang="ts" setup generic="T extends string | number | boolean">
import { WLSegmentBlock } from "../segment-block";
import { computed, ref, watch, nextTick, useTemplateRef } from "vue";
import {
  useResizeObserver,
  useIntersectionObserver,
  useElementSize,
  useDebounceFn,
} from "@vueuse/core";
import {
  useRoute,
  useRouter,
  type RouteLocationAsPath,
  type RouteLocationAsRelative,
  type RouteLocationAsString,
  type RouteLocationResolved,
} from "vue-router";
import { type SegmentProps } from "./types";
import { useWebHaptics } from "../../composables/use-web-haptics";

export type SegmentBlock = typeof WLSegmentBlock;

const {
  items = [],
  isFullWidth = false,
  variant = "default",
  isDisabled = false,
} = defineProps<SegmentProps<T>>();

const modelValue = defineModel<T>({ required: true });

const route = useRoute();
const router = useRouter();
const { triggerHaptic } = useWebHaptics();

const onChange = (value: T): void => {
  if (isDisabled) return;
  if (modelValue.value === value) return;
  triggerHaptic("soft");
  modelValue.value = value;
};

const backgroundRef = useTemplateRef("backgroundRef");
const itemRefs = ref<Array<HTMLElement>>([]);
const segmentContainerRef = useTemplateRef("segmentContainerRef");

const currentItemIndex = computed(() => {
  return items.findIndex(({ value }) => modelValue.value === value);
});

const { width } = useElementSize(segmentContainerRef);

// Watch for when the segment becomes visible (e.g., in modals or expandables)
useIntersectionObserver(
  segmentContainerRef,
  ([{ isIntersecting }]) => {
    if (isIntersecting && itemRefs.value[currentItemIndex.value]) {
      // Use nextTick to ensure DOM is fully rendered
      nextTick(() => {
        transformBackground(itemRefs.value[currentItemIndex.value]);
      });
    }
  },
  { threshold: 0.1 },
);

const setInitial = (): void => {
  // Due to the `Icon` component being loaded asynchronously it will report a
  // `width` of `0` initially as it hasn't loaded yet.
  //
  // We use the `ResizeObserver` API to detect when the dimensions change as it
  // is loaded and update the background `width` accordingly.
  const index = items.findIndex(({ value }) => modelValue.value === value);
  const itemRef = itemRefs.value[index];
  if (!itemRef) return;

  const { stop } = useResizeObserver(itemRef, () => {
    transformBackground(itemRef);
    stop();
  });

  // Also trigger immediately in case the element is already properly sized
  transformBackground(itemRef);
};

const setItemRef = (ref: unknown): void => {
  const _ref = ref as SegmentBlock;
  // @ts-expect-error: Types are not exposed, and this component shouldn't have
  // direct knowledge of all the internals and implementation details.
  const _elRef = _ref?.elRef?.elRef?.$el || _ref?.elRef?.elRef;

  if (_elRef && itemRefs.value.find((el) => el === _elRef) === undefined) {
    itemRefs.value.push(_elRef as HTMLElement);
    setInitial();
  }
};

const withoutTrailingSlash = (path: string): string => {
  return path.endsWith("/") ? path.slice(0, -1) : path;
};

/**
 * Animates background to match the `width` and `left` position for `element`.
 *
 * @param element - The element the background will inherit from
 * @param duration - Animation duration in milliseconds
 */
const transformBackground = (element: HTMLElement, duration = 200): void => {
  if (!element || !backgroundRef.value || !element.parentElement) {
    return;
  }

  const parentRect = element.parentElement.getBoundingClientRect();
  const childRect = element.getBoundingClientRect();
  const left = childRect.left - parentRect.left - childRect.width / 2;
  const width = childRect.width;

  backgroundRef.value.style.transitionDuration = `${duration}ms`;
  backgroundRef.value.style.left = left + "px";
  backgroundRef.value.style.width = width + "px";
};

const debouncedTransformBackground = useDebounceFn(() => {
  if (!itemRefs.value[currentItemIndex.value]) return;
  transformBackground(itemRefs.value[currentItemIndex.value]);
}, 150);

/**
 * Safely resolve a `vue-router` path without unexpectedly `throw`-ing
 *
 * @param path The `vue-router` path object to resolve
 */
const safeResolve = (
  path: RouteLocationAsString | RouteLocationAsPath | RouteLocationAsRelative,
): RouteLocationResolved | undefined => {
  try {
    return router.resolve(path);
  } catch {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn(`[Segment.vue] could not resolve route "${path}"`);
    }
    return undefined;
  }
};

/* c8 ignore start */
watch(
  () => route.path,
  () => {
    const resolvedFrom = safeResolve(route.path);
    if (!resolvedFrom) return;

    const index = items.findIndex(({ to }) => {
      try {
        // If `to` was given as query params, e.g. `?modal=more/faq/stations`
        if (typeof to === "string" && to.startsWith("?")) {
          return route.fullPath.includes(to);
        } else if (to) {
          // Match a regular `to` link, e.g. `/search` or `/plan-journey`
          const resolvedTo = safeResolve(to);
          if (!resolvedTo) return false;

          return (
            withoutTrailingSlash(resolvedTo.path) ===
            withoutTrailingSlash(resolvedFrom.path)
          );
        }
      } catch {
        return false;
      }
    });

    if (index !== -1) {
      modelValue.value = items[index].value;
    }
  },
  { immediate: true },
);

watch(modelValue, (newValue, oldValue) => {
  let newIndex = -1;
  let oldIndex = -1;

  items.forEach((item, index) => {
    if (item.value === newValue) newIndex = index;
    if (item.value === oldValue) oldIndex = index;
  });

  if (newIndex === -1 || oldIndex === -1) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn(
        "[Segment.vue] could not transform background due to undefined index, got:",
        {
          newIndex,
          oldIndex,
        },
      );
    }
    return;
  }

  const indexDelta = Math.abs(oldIndex - newIndex);
  const durationMs = Math.min(indexDelta, 3) * 50 + 200;
  transformBackground(itemRefs.value[newIndex], durationMs);
});

watch(width, debouncedTransformBackground);

// Seems to get the last value when items changes, I suppose it has
// to do with dynamically adding/removing items via e.g. `v-if`?
watch(
  () => items,
  (newValue, oldValue) => {
    if (newValue.length !== oldValue.length) {
      itemRefs.value = [];
      const newLength = items.length - 1;
      if (currentItemIndex.value > newLength) {
        onChange(items[currentItemIndex.value].value);
      }
    }
  },
);
/* c8 ignore stop */
</script>

<style>
@reference "@theme";

.wl-segment {
  a {
    @apply no-underline;
  }

  .active-item {
    @apply bg-default shadow-down-sm;
  }

  &.variant-elevated {
    @apply box-border border border-solid border-transparent bg-default shadow-down-sm transition-shadow duration-200;

    .active-item {
      @apply bg-theme-transparent shadow-none;
    }
  }

  .wl-segment-block {
    &::after {
      @apply bg-emphasis absolute inset-y-0 right-[-2.5px] my-auto block h-3/4 w-px transition duration-300 ease-in-out content-[''];
    }
  }

  .wl-segment-block:last-child::after {
    @apply hidden;
  }

  .is-active,
  /* Targets the element before the active element */
  .wl-segment-block:has(+ .is-active) {
    &::after {
      @apply bg-transparent;
    }
  }
}
</style>
